import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import CommonSelect from "../../../../../core/common/commonSelect";
import { all_routes } from "../../../../router/all_routes";
import Cookies from "js-cookie";
import { useUserPermissions } from "../../../../UserPermissionsContext";

interface ErrorMessages {
  name?: string[];
  category?: string[];
  has_call_back_time?: string[];
  non_field_errors?: string[];
}

interface SelectOption {
  value: string;
  label: string;
}

const EditSedSubCategory = () => {
  const routes = all_routes;
  const navigate = useNavigate();
  const location = useLocation();
  const subCategoryId = location.state?.id;

  const jwtToken = Cookies.get("authToken");
  const { webApi, userPermissions } = useUserPermissions();

  const [isLoading, setIsLoading] = useState(false);
  const [pageLoading, setPageLoading] = useState(true);

  const [categories, setCategories] = useState<SelectOption[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<SelectOption | null>(
    null,
  );

  const [errorMessage, setErrorMessage] = useState<ErrorMessages>({});

  const [formData, setFormData] = useState({
    name: "",
    category: "",
    has_call_back_time: false,
  });

  const checkPermission = (action: any, subject: any) => {
    return userPermissions.some(
      (ability) => ability.action === action && ability.subject === subject,
    );
  };

  const headers = {
    "Content-Type": "application/json",
    Authorization: `Bearer ${jwtToken}`,
  };

  // Fetch categories
  const fetchCategoriesData = async () => {
    try {
      const response = await fetch(
        `${webApi}/sed/sed_ticket_category_dropdown_for_subcategory_master/`,
        {
          method: "GET",
          headers,
        },
      );

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.detail || "Failed to fetch categories");
      }

      const transformed = result.map((item: { id: number; name: string }) => ({
        value: String(item.id),
        label: item.name,
      }));

      setCategories(transformed);
      return transformed;
    } catch (error) {
      console.error(error);
      return [];
    }
  };

  // Fetch subcategory details
  const fetchSubCategory = async (categoryOptions: SelectOption[]) => {
    try {
      const response = await fetch(
        `${webApi}/sed/sed_ticket_subcategory/${subCategoryId}/`,
        {
          method: "GET",
          headers,
        },
      );

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.detail || "Failed to fetch subcategory");
      }

      setFormData({
        name: result.name || "",
        category: String(result.category || ""),
        has_call_back_time: result.has_call_back_time || false,
      });

      const matchedCategory = categoryOptions.find(
        (item) => item.value === String(result.category),
      );

      if (matchedCategory) {
        setSelectedCategory(matchedCategory);
      }
    } catch (error) {
      console.error(error);
    } finally {
      setPageLoading(false);
    }
  };

  useEffect(() => {
    if (!checkPermission("change_sedticketcategory", "sedticketcategory")) {
      navigate(routes.crmLandingPage);
    } else {
      const init = async () => {
        const categoryOptions = await fetchCategoriesData();
        await fetchSubCategory(categoryOptions);
      };
      if (subCategoryId) {
        init();
      }
    }
  }, [userPermissions]);

  const handleCategoryChange = (selected: SelectOption | null) => {
    setSelectedCategory(selected);

    setFormData((prev) => ({
      ...prev,
      category: selected?.value || "",
    }));

    if (errorMessage.category) {
      setErrorMessage((prev) => ({ ...prev, category: undefined }));
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData((prev) => ({
      ...prev,
      name: e.target.value,
    }));

    if (errorMessage.name) {
      setErrorMessage((prev) => ({ ...prev, name: undefined }));
    }
  };

  // Update API
  const updateData = async () => {
    const response = await fetch(
      `${webApi}/sed/sed_ticket_subcategory/${subCategoryId}/`,
      {
        method: "PUT", // or PATCH based on backend
        headers,
        body: JSON.stringify({
          name: formData.name,
          category: Number(formData.category),
          has_call_back_time: formData.has_call_back_time,
        }),
      },
    );

    const result = await response.json();

    if (!response.ok) {
      setErrorMessage(result);
      throw new Error(result.detail || "Failed");
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      await updateData();
      navigate(routes.SEDSubCategory);
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  // if (pageLoading) {
  //   return <div>Loading...</div>;
  // }

  return (
    <div className="page-wrapper">
      <div className="content container-fluid">
        <div className="page-header">
          <div className="row">
            <div className="col-sm-12">
              <h3 className="page-title">SED Edit Sub Category</h3>
              <ul className="breadcrumb">
                <li className="breadcrumb-item">
                  <Link to={routes.SEDDashBoard}>Dashboard</Link>
                </li>
                <li className="breadcrumb-item">
                  <Link to={routes.SEDSubCategory}>Sub Categories</Link>
                </li>
                <li className="breadcrumb-item active">Edit Sub Category</li>
              </ul>
            </div>
          </div>
        </div>

        <div className="row justify-content-center">
          <div className="col-sm-12 d-flex justify-content-center">
            <div
              className="card"
              style={{
                width: "70%",
                boxShadow: "0 4px 8px rgba(0,0,0,0.2)",
                borderRadius: "10px",
              }}
            >
              <div className="card-body">
                <form onSubmit={handleSubmit}>
                  <div className="mb-3 me-3" style={{ width: "35%" }}>
                    <label className="form-label">Category</label>
                    <CommonSelect
                      className="select"
                      options={categories}
                      onChange={handleCategoryChange}
                      value={selectedCategory}
                      isRequired={true}
                    />
                    {errorMessage.category && (
                      <small className="text-danger">
                        {errorMessage.category[0]}
                      </small>
                    )}
                  </div>

                  <div className="form-group mb-3" style={{ width: "35%" }}>
                    <label className="form-label">Sub Category Name</label>
                    <input
                      type="text"
                      className={`form-control ${
                        errorMessage.name ? "is-invalid" : ""
                      }`}
                      name="name"
                      placeholder="Sub Category Name"
                      onChange={handleInputChange}
                      value={formData.name}
                      // required
                    />
                    {errorMessage.name && (
                      <div className="invalid-feedback">
                        {errorMessage.name[0]}
                      </div>
                    )}
                    {errorMessage.non_field_errors && (
                      <div className="invalid-feedback">
                        {errorMessage.non_field_errors[0]}
                      </div>
                    )}
                  </div>

                  <div className="form-check mb-4">
                    <input
                      type="checkbox"
                      className="form-check-input"
                      checked={formData.has_call_back_time}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          has_call_back_time: e.target.checked,
                        }))
                      }
                    />
                    <label className="form-check-label">
                      Has Callback Time
                    </label>
                  </div>

                  <div className="modal-footer">
                    <Link
                      className="btn btn-primary"
                      to={routes.SEDSubCategory}
                      style={{
                        backgroundColor: "lightcoral",
                        color: "white",
                        marginRight: "5px",
                      }}
                    >
                      Cancel
                    </Link>

                    <button
                      className="btn btn-primary"
                      type="submit"
                      disabled={isLoading}
                    >
                      {isLoading ? (
                        <i className="spinner-border spinner-border-sm" />
                      ) : (
                        "Update"
                      )}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EditSedSubCategory;
